/**
 * Typed accessor for the shell's build-time environment — the ONLY module permitted to read
 * `import.meta.env` (enforced by eslint.config.js).
 *
 * The shell is one document serving every AIRE module, so it reads one Okta OIDC config (the shared
 * platform app — auth.md §5) plus one API base per module that needs a distinct backend origin:
 *  - `VITE_API_BASE_URL` — the platform API origin (through the CloudFront `/api/*` proxy; CSRF +
 *    refresh-cookie path). Legacy values ending in `/api/close` are normalized to the origin.
 *  - `VITE_AIRE_ADMIN_API_BASE` — the admin API (API Gateway behind a JWT authorizer expecting
 *    `Authorization: Bearer <id token>`).
 *  - `VITE_AIRE_PLANNING_API_BASE` — the platform origin used for planning routes (I07-2,
 *    LIO-29259). Same JWT-authorizer shape as admin's (`requirePlanningUser` reads
 *    `Authorization: Bearer <id token>` exactly like the admin API does). Legacy values ending in
 *    `/api/close` or `/api/planning` are normalized to prevent duplicated route prefixes.
 *
 * Security posture: the Okta OIDC app is provisioned out-of-band; issuer/clientId/redirectUri are
 * injected from `VITE_OKTA_*` at build time and never hardcoded.
 *
 * Production builds fail fast on a missing required value rather than silently falling back to a
 * relative path that would resolve to the SPA `index.html` (the original BUG-1 symptom). In dev and
 * tests an empty value is allowed: requests use relative `/api/...` paths that the Vite dev server
 * proxies to the local module APIs (see vite.config.ts).
 */

export type AireEnvironment = "local" | "dev" | "uat" | "prod";

export interface ShellEnv {
  readonly environment: AireEnvironment;
  readonly oktaIssuer: string;
  readonly oktaClientId: string;
  readonly oktaRedirectUri: string;
  /** Platform API origin used by close read and variance control routes. */
  readonly closeApiBaseUrl: string;
  /** Admin API base. Empty in dev/tests (relative path + proxy). */
  readonly adminApiBase: string;
  /** Platform origin for planning API routes (I07-2). Empty in dev/tests (relative path + proxy). */
  readonly planningApiBase: string;
  /** Synthetic identity for the local planning loop. Never permitted in a production build. */
  readonly localAuthEnabled: boolean;
}

function readTrimmed(metaEnv: ImportMetaEnv, name: string): string {
  const value: unknown = (metaEnv as Record<string, unknown>)[name];
  return typeof value === "string" ? value.trim() : "";
}

function normalizePlatformApiBase(value: string): string {
  const withoutTrailingSlash = value.replace(/\/+$/u, "");
  return withoutTrailingSlash.endsWith("/api/close")
    ? withoutTrailingSlash.slice(0, -"/api/close".length)
    : withoutTrailingSlash;
}

function normalizePlanningApiBase(value: string): string {
  const platformBase = normalizePlatformApiBase(value);
  return platformBase.endsWith("/api/planning")
    ? platformBase.slice(0, -"/api/planning".length)
    : platformBase;
}

export function loadShellEnv(metaEnv: ImportMetaEnv = import.meta.env): ShellEnv {
  const isProd = metaEnv.PROD === true;
  const configuredEnvironment = readTrimmed(metaEnv, "VITE_AIRE_ENVIRONMENT").toLowerCase();
  const environment: AireEnvironment =
    configuredEnvironment === ""
      ? "local"
      : configuredEnvironment === "dev" ||
          configuredEnvironment === "uat" ||
          configuredEnvironment === "prod" ||
          configuredEnvironment === "local"
        ? configuredEnvironment
        : (() => {
            throw new Error(
              `Invalid VITE_AIRE_ENVIRONMENT "${configuredEnvironment}". Expected local, dev, uat, or prod.`,
            );
          })();

  const oktaIssuer = readTrimmed(metaEnv, "VITE_OKTA_ISSUER");
  const oktaClientId = readTrimmed(metaEnv, "VITE_OKTA_CLIENT_ID");
  const oktaRedirectUri = readTrimmed(metaEnv, "VITE_OKTA_REDIRECT_URI");
  const configuredApiBaseUrl = readTrimmed(metaEnv, "VITE_API_BASE_URL");
  const closeApiBaseUrl = normalizePlatformApiBase(configuredApiBaseUrl);
  const adminApiBase = readTrimmed(metaEnv, "VITE_AIRE_ADMIN_API_BASE");
  const planningApiBase = normalizePlanningApiBase(
    readTrimmed(metaEnv, "VITE_AIRE_PLANNING_API_BASE"),
  );
  const localAuthRequested = readTrimmed(metaEnv, "VITE_AIRE_LOCAL_AUTH_ENABLED") === "true";
  const localAuthEnabled = metaEnv.DEV === true && environment === "local" && localAuthRequested;

  if (localAuthRequested && !localAuthEnabled) {
    throw new Error(
      "VITE_AIRE_LOCAL_AUTH_ENABLED may only be enabled by a local Vite development server.",
    );
  }

  if (isProd) {
    const required: Record<string, string> = {
      VITE_OKTA_ISSUER: oktaIssuer,
      VITE_OKTA_CLIENT_ID: oktaClientId,
      VITE_OKTA_REDIRECT_URI: oktaRedirectUri,
      VITE_API_BASE_URL: configuredApiBaseUrl,
      VITE_AIRE_ADMIN_API_BASE: adminApiBase,
      VITE_AIRE_PLANNING_API_BASE: planningApiBase,
      VITE_AIRE_ENVIRONMENT: configuredEnvironment,
    };
    const missing = Object.entries(required)
      .filter(([, value]) => value === "")
      .map(([name]) => name);
    if (missing.length > 0) {
      throw new Error(
        `Missing required environment variable(s) ${missing.join(", ")} for this production shell ` +
          "build. The shell authenticates via the shared Okta OIDC app and calls module APIs on " +
          "their own origins (close + admin + planning); set these at build time (the OIDC app is " +
          "provisioned separately, never hardcoded).",
      );
    }
  }

  return {
    environment,
    oktaIssuer,
    oktaClientId,
    oktaRedirectUri,
    closeApiBaseUrl,
    adminApiBase,
    planningApiBase,
    localAuthEnabled,
  };
}
